import type { BillRecurrence } from "../api-modules/types.js";

/**
 * Cost-per-use estimator. Pure function — no IO. Web mirror of
 * `shared/src/util/cost-per-use.ts` (5.1 lock — web doesn't import
 * @hearth/shared). See shared module for full design rationale.
 */

export interface CostPerUseInput {
  readonly amount_cents: number;
  readonly recurrence: BillRecurrence;
  readonly expected_uses_per_month?: number;
  readonly category?: string;
}

export interface CostPerUseResult {
  readonly monthly_cost_cents: number;
  readonly uses_per_month: number;
  readonly cost_per_use_cents: number;
  readonly used_default: boolean;
}

const KEYWORD_DEFAULTS: ReadonlyArray<readonly [string, number]> = [
  ["streaming", 30],
  ["netflix", 30],
  ["hulu", 30],
  ["prime video", 30],
  ["spotify", 30],
  ["apple music", 30],
  ["youtube premium", 30],
  ["disney", 25],
  ["max", 25],
  ["paramount", 25],
  ["peacock", 25],
  ["gym", 12],
  ["fitness", 12],
  ["yoga", 8],
  ["pilates", 8],
  ["software", 20],
  ["saas", 20],
  ["adobe", 20],
  ["github", 20],
  ["takeout", 8],
  ["delivery", 6],
  ["gaming", 15],
  ["games", 15],
  ["cloud storage", 10],
  ["news", 10],
  ["new york times", 12],
  ["wall street journal", 6],
];

const FALLBACK_USES_PER_MONTH = 30;

export function costPerUse(input: CostPerUseInput): CostPerUseResult | null {
  if (input.recurrence === "one_time") return null;

  const monthly = monthlyCost(input.amount_cents, input.recurrence);
  let usesPerMonth = input.expected_uses_per_month;
  let usedDefault = false;
  if (usesPerMonth === undefined || usesPerMonth <= 0) {
    usesPerMonth = inferUsesFromCategory(input.category);
    usedDefault = true;
  }

  const denom = usesPerMonth <= 0 ? 1 : usesPerMonth;
  const costPer = Math.round(monthly / denom);

  return {
    monthly_cost_cents: monthly,
    uses_per_month: usesPerMonth,
    cost_per_use_cents: costPer,
    used_default: usedDefault,
  };
}

function monthlyCost(amountCents: number, recurrence: BillRecurrence): number {
  switch (recurrence) {
    case "monthly":
      return amountCents;
    case "quarterly":
      return Math.round(amountCents / 3);
    case "annual":
      return Math.round(amountCents / 12);
    case "one_time":
      return amountCents;
  }
}

export function inferUsesFromCategory(category?: string): number {
  if (!category) return FALLBACK_USES_PER_MONTH;
  const lower = category.toLowerCase();
  for (const [kw, n] of KEYWORD_DEFAULTS) {
    if (lower.includes(kw)) return n;
  }
  return FALLBACK_USES_PER_MONTH;
}
